/**
 * Prize allocation status — the FRONTEND HALF of the contract.
 *
 * These types mirror api_backend_fastify_server/src/domain/prize-allocation-status.ts.
 * Note what is NOT here: the classifier. The backend decides whether an entry is
 * locked / potential / excluded and ships the answer; the UI only renders it.
 * That is deliberate — a second classifier on this side would drift from the
 * one the prize engine actually uses, and the green/orange/red numbers would
 * stop agreeing with the money the app pays out.
 *
 * If you find yourself about to write `if (reg.cancelled) return 'excluded'`
 * anywhere in this app: don't. Ask the API.
 */

export type PrizeAllocationStatus = 'locked' | 'potential' | 'excluded';

export type PrizeExclusionReason =
  | 'cancelled'
  | 'did_not_perform'
  | 'drawn_out'
  | 'removed_from_prize_pool';

export type PrizeEntryFlag = 'drawn_out' | 'did_not_perform' | 'unplaced';

export const PRIZE_STATUS_LABELS: Record<PrizeAllocationStatus, string> = {
  locked: 'Locked in',
  potential: 'Potential',
  excluded: 'Not included',
};

export const PRIZE_STATUS_DESCRIPTIONS: Record<PrizeAllocationStatus, string> = {
  locked:
    'In the prize pool and settled — the draw is final and these contestants have a spot.',
  potential:
    'In the prize pool but not settled yet — the draw is not final, or these contestants did not get a spot.',
  excluded:
    'Not in the prize pool — contestants who did not compete (cancelled, drew out, or did not perform), plus anyone an admin removed by hand.',
};

export const PRIZE_EXCLUSION_REASON_LABELS: Record<PrizeExclusionReason, string> = {
  cancelled: 'Entry cancelled',
  did_not_perform: 'Did not perform',
  drawn_out: 'Drew out of the draw',
  removed_from_prize_pool: 'Removed from prize pool by an admin',
};

export const PRIZE_ENTRY_FLAG_LABELS: Record<PrizeEntryFlag, string> = {
  drawn_out: 'Drew out',
  did_not_perform: 'Did not perform',
  unplaced: 'Not placed in the draw',
};

/**
 * Tailwind classes per status.
 *
 * Colour is a REDUNDANT cue here, never the only one: every status also carries
 * its text label and an icon. A red/green-blind admin reading this page is
 * looking at contestant money — "the red ones" cannot be the only way to tell.
 */
export const PRIZE_STATUS_STYLES: Record<
  PrizeAllocationStatus,
  { badge: string; text: string; bar: string; dot: string }
> = {
  locked: {
    badge:
      'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800',
    text: 'text-emerald-700 dark:text-emerald-400',
    bar: 'bg-emerald-500',
    dot: 'bg-emerald-500',
  },
  potential: {
    badge:
      'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800',
    text: 'text-amber-700 dark:text-amber-400',
    bar: 'bg-amber-500',
    dot: 'bg-amber-500',
  },
  excluded: {
    badge:
      'bg-red-100 text-red-800 border-red-300 dark:bg-red-950 dark:text-red-300 dark:border-red-800',
    text: 'text-red-700 dark:text-red-400',
    bar: 'bg-red-500',
    dot: 'bg-red-500',
  },
};
